// Games index collection.
import { gameLibrary } from '../../configs/gameLibrary';
import { formatUtcMmDdYyyyDate } from '../../utilities/formatDate';
import { findGameDiscoverySlug } from '../../utilities/getGames';
import { collectionSection, defineCollection, facts, link, p, table, text, } from '../schema';
// Only surface games that have actually released. Unreleased entries still
// live in the library (for recipes, catalog gating, etc.) but must not appear
// on the public discover index. Evaluated at build time, which matches how the
// rest of the static content system resolves dates.
const now = new Date();
const releasedGames = gameLibrary
    .filter((game) => game.gameWiki.releasedAt && game.gameWiki.releasedAt <= now)
    .sort((a, b) => a.gameCore.gameName.localeCompare(b.gameCore.gameName, undefined, {
    sensitivity: 'base',
}));
const gameLibraryTable = table({
    columns: [
        'Game Name',
        'Game Type',
        'Min Players',
        'Max Players',
        'Release Date',
    ],
    rows: releasedGames.map((game) => {
        const slug = findGameDiscoverySlug(game.gameCore.gameId);
        return [
            slug
                ? [link(`/discover/games/${slug}`, game.gameCore.gameName)]
                : `${game.gameCore.gameName} (coming soon)`,
            game.gameWiki.gameType,
            String(game.gameCore.gamePlayersMin),
            String(game.gameCore.gamePlayersMax),
            game.gameWiki.releasedAt
                ? formatUtcMmDdYyyyDate(game.gameWiki.releasedAt)
                : 'Coming Soon',
        ];
    }),
});
export const gamesCollection = defineCollection({
    id: 'collection:games',
    kind: 'index',
    route: '/discover/games',
    title: 'Our Games',
    description: 'Explore party games shaped by the people who play them, with fresh content generated every time you play.',
    seo: {
        description: 'Explore party games for streamers and their communities. Each game generates fresh content, keeping every session personal and replayable.',
    },
    hero: {
        eyebrow: [text('Discover')],
        title: [text('Our Games')],
        description: [
            text('Airhead Games are shaped by the people who play them. Fresh every session, frictionless to join.'),
        ],
        imageAsset: 'gameLibraryCozy',
        imageAlt: 'Airhead Games welcome scene',
        showDescription: true,
        background: 'gradient-card',
        backgroundAccentFamily: 'red',
    },
    sections: [
        collectionSection({
            id: 'overview',
            title: 'Overview',
            blocks: [
                p('Welcome to Airhead Games, a collection of party games that adapt to your group and never play the same way twice. Streamers use our platform to meaningfully engage their audience and build a community worth coming back to.'),
                p([
                    text('Getting started is easy: select a game, invite your chat, and start playing. Only the host needs '),
                    link('/discover/features/loons', 'Loons'),
                    text(" and all other players simply join from their device's web browser via link or QR code — no downloads or accounts required. Games are designed to be quick with each play-through taking about 10-20 minutes to complete."),
                ]),
                p('Airhead Games is run by a solo developer guided by curiosity, community feedback, and the goal of strengthening groups through laughter, creativity, and shared play.'),
            ],
        }),
        collectionSection({
            id: 'facts',
            title: 'Facts',
            blocks: [
                facts({
                    items: [
                        { label: 'Release Date', value: 'September 23, 2025' },
                        { label: 'Genre', value: 'Party Games' },
                        { label: 'Platforms', value: 'Web Browser' },
                        { label: 'Players', value: '1-16 players' },
                        { label: 'Duration', value: '10-20 minutes' },
                    ],
                }),
            ],
        }),
        collectionSection({
            id: 'game-library',
            title: 'Game Library',
            blocks: [
                p('Our library features a variety of party games that never play the same way twice. Each game generates new content every turn, ensuring fresh experiences every time you play. New games are added regularly.'),
                gameLibraryTable,
            ],
        }),
    ],
});
//# sourceMappingURL=games.js.map